<template>
  <el-form
    @submit.prevent="validateSubmit"
    novalidate
    :model="inventoryTransfer"
    ref="inventoryTransferForm"
    :rules="rules"
    label-position="top"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit('inventoryTransferForm')"
          />
        </div>

        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('inventoryTransferForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section class="my-2 p-2">
      <h1>{{ t('sections.info') }}</h1>
      <input-group class="items-center">
        <el-form-item :label="t('fromLocation')" class="input-label">
          <el-input
            :value="$ctx.locale === 'en' ? $ctx.currentLocation.enName : $ctx.currentLocation.arName"
            disabled
            class="w-full"
          />
        </el-form-item>
        
        <el-form-item :label="t('toLocation')" prop="toLocationID" class="input-label">
          <el-select v-model="inventoryTransfer.toLocationID" filterable clearable class="w-full">
            <el-option
              v-for="item in locations"
              :key="item.id"
              :label="$ctx.locale === 'en' ? item.enName : item.arName"
              :value="item.id"
            >
              <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">{{
                $ctx.locale === 'en' ? item.enName : item.arName
              }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('transferOutType')" class="input-label" prop="transferOutType">
          <el-tooltip
            class="item"
            effect="dark"
            :content="t('tooltips.transferOutType')"
            placement="bottom"
          >
            <el-select v-model="inventoryTransfer.transferOutType" class="w-full">
              <!-- indirect transfer is currently not supported -->
              <el-option v-for="item in ['direct']" :key="item" :label="t(item)" :value="item">
                <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">{{ t(item) }}</span>
              </el-option>
            </el-select>
          </el-tooltip>
        </el-form-item>
      </input-group>

              <el-form-item :label="t('notes')" class="input-label">
          <el-input
            id="notes"
            v-model="inventoryTransfer.reason"
            :placeholder="t('placeholders.notes')"
            clearable
            type="textarea"
            :rows="3"
          >
          </el-input>
        </el-form-item>
    </input-section>

    <input-section>
      <h1>{{ t('sections.inventory') }}</h1>
      <el-form-item :label="t('itemName')" class="input-label">
        <el-autocomplete
          id="item"
          v-model="itermSearchTerm"
          :fetch-suggestions="searchItems"
          value-key="name"
          :trigger-on-focus="false"
          class="w-full"
          @select="addItem"
          @keypress.enter.prevent="pressedEnter = true"
          highlight-first-item
        >
        </el-autocomplete>
      </el-form-item>
    </input-section>

    <div class="w-4/5 mt-8" v-if="!inventoryTransfer.empty()">
      <el-table :data="inventoryTransfer.inventoryTransferItems" style="width: 100%" stripe>
        <el-table-column min-width="200" prop="item.name" :label="t('itemName')"> </el-table-column>

        <el-table-column :label="t('currentQuantity')" width="130" align="center">
          <template #default="props">
            <div class="text-center">
              <template v-if="props.row.item.isExpirable">
                <span v-if="props.row.extraProperties?.batchNumber">
                  {{ computedStock(props.row).quantity - props.row.quantity }}
                </span>
                <span v-else class="text-red-500 text-xs">{{ t('selectBatchFirst') }}</span>
              </template>
              <template v-else>
                {{ computedStock(props.row).quantity - props.row.quantity }}
              </template>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="extraProperties.batchNumber" :label="t('batchNumber')"
                         width="200" align="center">
          <template #default="props">
            <el-select 
              v-if="props.row.item.isExpirable" 
              v-model="props.row.extraProperties.batchNumber" 
              :placeholder="t('batchNumber')" 
              class="w-full"
              @change="onBatchChange(props.row)"
            >
              <template v-for="stock in props.row.item.itemStocks" :key="stock.extraProperties?.batchNumber">
                <el-option 
                  v-if="stock.extraProperties?.batchNumber" 
                  :label="stock.extraProperties?.batchNumber" 
                  :value="stock.extraProperties?.batchNumber" 
                />
              </template>
            </el-select>
            <span v-else class="text-gray-400">{{ t('noBatch') }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="quantity" :label="t('sentQuantity')" width="130" align="center">
          <template #default="props">
            <template v-if="props.row.item.isExpirable">
              <el-input-number
                v-if="props.row.extraProperties?.batchNumber"
                v-model="props.row.quantity"
                :step="1"
                size="small"
                :min="0"
                :max="computedStock(props.row).quantity"
                controls-position="right"
                class="w-full"
              ></el-input-number>
              <span v-else class="text-red-500 text-xs">{{ t('selectBatchFirst') }}</span>
            </template>
            <template v-else>
              <el-input-number
                v-model="props.row.quantity"
                :step="1"
                size="small"
                :min="0"
                :max="computedStock(props.row).quantity"
                controls-position="right"
                class="w-full"
              ></el-input-number>
            </template>
          </template>
        </el-table-column>

        <el-table-column
          prop="inventoryTransferItemSerials"
          :label="t('serialNumbers')"
          min-width="300"
        >
          <template #default="props">
            <template v-if="props.row.item.isSerialized">
              <template v-if="!props.row.item.isExpirable || props.row.extraProperties?.batchNumber">
                <!-- select serials to transfer -->
                <div class="w-full">
                  <div class="text-xs mb-2 text-gray-600">
                    {{
                      t('insertSerials', {
                        num: props.row.quantity
                      })
                    }}
                  </div>
                  <el-select
                    filterable
                    clearable
                    multiple
                    class="w-full"
                    v-model="props.row.toSendSerialIDs"
                    :multiple-limit="props.row.quantity"
                    :placeholder="t('selectSerials')"
                  >
                    <el-option
                      v-for="(item, i) in getAvailableSerials(props.row)"
                      :key="i"
                      :label="item.number"
                      :value="item.id"
                    >
                      <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                        {{ item.number }}
                      </span>
                    </el-option>
                  </el-select>
                </div>
              </template>
              <template v-else>
                <span class="text-red-500 text-xs">{{ t('selectBatchFirst') }}</span>
              </template>
            </template>
            <span v-else class="text-gray-400 text-xs">{{ t('noSerials') }}</span>
          </template>
        </el-table-column>

        <el-table-column width="70" align="center">
          <template #default="scope">
            <span class="text-red-600 cursor-pointer" @click="removeItem(scope.$index)">
              <font-awesome-icon icon="trash" />
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Cashier PIN Dialog -->
    <el-dialog v-model="cashierPinDialogOpen" width="30%" :title="t('enterCashierPin')">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('enterCashierPin') }}</p>
        </div>

        <!-- password input up to 4 chars -->
        <div class="my-2">
          <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            @keyup.enter="submitWithPin"
            ref="cashierPinInput"
          />
        </div>

        <!-- submit button -->
        <div class="flex items-center justify-between mb-2">
          <el-button 
            type="primary" 
            @click="submitWithPin"
            class="w-full"
          >
            {{ t('submitTransfer') }}
          </el-button>
        </div>
      </div>
    </el-dialog>
  </el-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import InventoryTransfer, {
  inventoryTransferI18nMessages
} from '@/models/inventory/InventoryTransfer'
import Location from '@/models/company/Location'
import { ElForm } from 'element-plus'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import InputGroup from '@/components/form/InputGroup.vue'
import Item from '@/models/inventory/Item'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'new-inventory-receiving',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...inventoryTransferI18nMessages.en,

          sections: {
            info: 'Meta data',
            inventory: 'Inventory Info'
          },

          notes: 'Notes',
          placeholders: {
            notes: 'Optional notes about this transfer',
            itemName: 'Enter barcode, sku, or item name'
          },

          userTransfering: 'Employee responsible for this inventory transfer',
          insertSerials: 'You must insert {num} serial numbers',

          currentQuantity: 'Quantity left',
          sentQuantity: 'Sent quantity',
          batchNumber: 'Batch Number',
          noBatch: 'No batch',
          selectBatchFirst: 'Select batch first',
          selectSerials: 'Select serial numbers',
          noSerials: 'No serials required',

          successMsg: 'Inventory was transfered successfully',

          transferOutType: 'Choose transfer type',
          direct: 'Direct Transfer',
          indirect: 'Indirect Transfer',

          tooltips: {
            transferOutType: 'With direct transfer, the '
          },

          enterCashierPin: 'Enter cashier PIN',
          errCashierPinRequired: 'Cashier PIN is required',
          submitTransfer: 'Submit'
        },
        ar: {
          ...inventoryTransferI18nMessages.ar,

          sections: {
            info: 'البيانات الوصفية',
            inventory: 'البضاعة المنقولة'
          },

          notes: 'ملاحظات',
          placeholders: {
            notes: 'ملاحظات اختيارية حول عملية النقل',
            itemName: 'ادخل اسم المنتج او الباركود او SKU'
          },

          userTransfering: 'الموظف المسؤول عن نقل البضاعة',
          insertSerials: 'قم بادخال {num} serial numbers',

          currentQuantity: 'الكمية المتبقية',
          sentQuantity: 'الكمية المرسلة',
          batchNumber: 'رقم الوجبة',
          noBatch: 'بدون دفعة',
          selectBatchFirst: 'اختر الوجبة أولاً',
          selectSerials: 'اختر الأرقام التسلسلية',
          noSerials: 'لا يتطلب أرقام تسلسلية',

          successMsg: 'تم نقل البضاعة بنجاح',

          transferOutType: 'اختر نوع النقل',
          direct: 'مباشر',
          indirect: 'غير مباشر',

          tooltips: {
            transferOutType:
              'باستخدام النقل المباشر، تتحدث الكمية في الموقع المستلم بشكل مباشر. اما بالنقل الغير مباشر، فيجب استلام البضاعة من الموقع المستلم بشكل يدوي'
          },

          enterCashierPin: 'ادخل رمز الكاشير',
          errCashierPinRequired: 'رمز الكاشير مطلوب',
          submitTransfer: 'إرسال'
        }
      }
    })

    return { t }
  },

  components: {
    AlertBadge,
    HorizontalButton,
    InputSection,
    InputGroup
  },

  async beforeMount() {
    try {
      const locResp = await this.$http.get<Location[]>(Location.ENDPOINT)
      this.locations = locResp
        .map((loc) => Location.from(loc))
        // exclude this location
        .filter((loc) => loc.id !== this.$ctx.currentLocation.id)
    } catch (error) {
      this.error.title = error?.title
      this.error.body = error?.body
    }
  },

  data() {
    const inventoryTransfer = new InventoryTransfer('out')
    // Set the fromLocationID to the current location
    if (this.$ctx.currentLocation?.id) {
      inventoryTransfer.fromLocationID = this.$ctx.currentLocation.id
    }
    
    return {
      inventoryTransfer,
      itermSearchTerm: '',
      error: { title: '', body: '' },
      serials: [],
      pressedEnter: false,
      user: this.$ctx.currentUser,
      locations: new Array<Location>(),
      cashierPinDialogOpen: false,
      cashierPin: '',
      rules: {
        transferOutType: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ],
        toLocationID: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  methods: {
    computedStock(row: any) {
      if (row.item.isExpirable) {
        if (row.extraProperties?.batchNumber) {
            return row.item.itemStocks.find(stock => stock.extraProperties?.batchNumber === row.extraProperties.batchNumber)
        }
      }
      return row.item.itemStocks[0]
    },
    async searchItems(query: string, cb: Function) {
      // if an item already exists in the table, don't do remote search
      const cachedItem =
        this.inventoryTransfer.findByBarcode(query) || this.inventoryTransfer.findBySKU(query)
      if (cachedItem) {
        this.addItem(cachedItem)
        cb([])
        return
      }

      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
        const items = await this.$http.get<Item[]>(url)

        // when entering a barcode, the scanner hits enter by default
        // so if we get back one result, we wanna append that right away
        // it makes the process much faster than make the user select by themselves
        if (this.pressedEnter && items.length === 1) {
          this.addItem(items[0])
          cb([])
        } else {
          // show them all options
          cb(items)
        }
      } catch (error) {
        this.$alertModal.showDanger({ title: error.title })
        cb([])
      }

      this.pressedEnter = false
    },

    addItem(selected: Item) {
      this.inventoryTransfer.addItem(selected)
      this.itermSearchTerm = ''
    },

    removeItem(index: number) {
      this.inventoryTransfer.removeItem(index)
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
      this.inventoryTransfer = new InventoryTransfer('out')
      // Ensure fromLocationID is set when clearing the form
      if (this.$ctx.currentLocation?.id) {
        this.inventoryTransfer.fromLocationID = this.$ctx.currentLocation.id
      }
    },

    async submit(ir: InventoryTransfer, pin: string = '') {
      this.error.title = ''
      this.error.body = ''

      try {
        const headers = pin ? { pin: pin } : {}
        await this.$http.post(InventoryTransfer.ENDPOINT, ir, { headers })
        await this.$router.push(this.$Route.INVENTORY_INVENTORY_TRANSFERS)
      } catch (error: any) {
        return error
      }
    },

    async submitWithPin() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.$alertModal.showDanger({
            title: this.t('errCashierPinRequired')
          })
          return
        }
      }

      this.cashierPinDialogOpen = false
      const resp = await this.submit(this.inventoryTransfer as InventoryTransfer, this.cashierPin)

      if (resp) {
        this.error.title = resp?.title || 'Error'
        this.error.body = resp?.body || 'An error occurred'
        document.getElementById('main')?.scrollTo({ top: 0 })
      } else {
        this.clearForm('inventoryTransferForm')
        this.$alertModal.showSuccess({ title: this.t('successMsg') })
      }

      // Clear PIN for security
      this.cashierPin = ''
    },

    checkForm() {
      const errors = []
      for (const iai of this.inventoryTransfer.inventoryTransferItems) {
        errors.push(iai.validateSerials())
        errors.push(iai.validateQuantity())
      }

      this.error.body = errors.filter((err) => err).join('\n')

      if (this.error.body) {
        this.error.title = this.$t('validation.inputErrors')
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false
      }

      return true
    },

    async validateSubmit(formName: string) {
      this.inventoryTransfer.prepareSendPayloadData()
      const form = this.$refs[formName] as typeof ElForm

      form?.validate(async (valid: boolean) => {
        if (!valid || !this.checkForm()) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        // Check if PIN is required
        if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
          this.cashierPinDialogOpen = true
          this.$nextTick(() => {
            setTimeout(() => {
              this.getCashierInputRef().focus()
            }, 0)
          })
          return
        }

        // Submit without PIN
        const resp = await this.submit(this.inventoryTransfer as InventoryTransfer)

        if (resp) {
          this.error.title = resp?.title || 'Error'
          this.error.body = resp?.body || 'An error occurred'
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          this.clearForm(formName)
          this.$alertModal.showSuccess({ title: this.t('successMsg') })
        }
        return true
      })
    },

    getCashierInputRef() {
      return (this.$refs['cashierPinInput'] as any)
    },

    onBatchChange(row: any) {
      // Clear serials when batch changes
      row.toSendSerialIDs = []
      // Reset quantity when batch changes
      row.quantity = 0
    },

    getAvailableSerials(row: any) {
      if (row.item.isExpirable && row.extraProperties?.batchNumber) {
        const stock = row.item.itemStocks.find(
          (stock: any) => stock.extraProperties?.batchNumber === row.extraProperties.batchNumber
        )
        return stock?.itemSerials || []
      } else if (!row.item.isExpirable) {
        return row.item.itemStocks[0]?.itemSerials || []
      }
      return []
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3;
}

form > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
